import axios from 'axios';
import {
  BookingRequest,
  BookingResponse,
  Availability,
  Room,
  RoomWithStats,
  RoomCreateRequest,
  RoomUpdateRequest,
  ClinicianAvailabilitySlot,
  PatientBookingCreate,
  PatientBookingResponse,
  BookingListItem
} from '../types/booking';
import { LoginRequest, RegisterRequest, AuthResponse, User, UserUpdateRequest } from '../types/auth';
import { 
  GlobalAvailability, 
  MemberAvailability, 
  WeeklyAvailability, 
  GlobalSchedule, 
  AvailabilityConflict, 
  AvailabilityDateSummary, 
  CreateMemberAvailabilityRequest,
  BulkAvailabilityRequest,
  DayOfWeek,
  BreakCreateRequest,
  BreakCreateResponse,
  BreakOccurrence,
  RemoveAvailabilityRangeParams,
  AvailabilityRangeDeleteResult,
} from '../types/availability';
import { AnalyticsSnapshot, CancellationReport, ClinicCalendar } from '../types/analytics';
import { rateLimiter } from '../utils/rateLimiter';
import { logger } from '../utils/logger';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Include cookies in requests
});

// Add token to requests if available with security validation and rate limiting
api.interceptors.request.use((config) => {
  // Rate limiting check
  const endpoint = config.url || '';
  const endpointType = endpoint.includes('/auth/') ? 'auth' : 'api';
  const rateLimitCheck = rateLimiter.checkRequest(endpoint, endpointType);
  
  if (!rateLimitCheck.allowed) {
    const error = new Error(rateLimitCheck.message || 'Rate limit exceeded');
    error.name = 'RateLimitError';
    (error as any).retryAfter = rateLimitCheck.retryAfter;
    logger.warn('API request blocked by rate limiter', { endpoint, endpointType, retryAfter: rateLimitCheck.retryAfter });
    throw error;
  }
  
  const token = sessionStorage.getItem('access_token');
  const timestamp = sessionStorage.getItem('token_timestamp');
  
  // Check token age (optional security: remove tokens older than 8 hours)
  if (token && timestamp) {
    const tokenAge = Date.now() - parseInt(timestamp);
    const maxAge = 8 * 60 * 60 * 1000; // 8 hours in milliseconds
    
    if (tokenAge > maxAge) {
      sessionStorage.removeItem('access_token');
      sessionStorage.removeItem('token_timestamp');
      window.location.href = '/'; // Force re-login
      return config;
    }
    
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  // Add CSRF token if available
  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
  if (csrfToken) {
    config.headers['X-CSRF-Token'] = csrfToken;
  }
  
  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle rate limiting errors
    if (error.name === 'RateLimitError') {
      logger.warn('API request rate limited', { 
        endpoint: error.config?.url,
        retryAfter: error.retryAfter 
      });
      return Promise.reject(error);
    }

    // Handle unauthorized requests
    if (error.response?.status === 401) {
      sessionStorage.removeItem('access_token');
      sessionStorage.removeItem('token_timestamp');
      window.location.href = '/';
      return Promise.reject(error);
    }

    // Handle CSRF errors
    if (error.response?.status === 403 && error.response?.data?.message?.includes('CSRF')) {
      logger.error('CSRF token validation failed', error);
      return Promise.reject(new Error('Security validation failed. Please refresh the page.'));
    }

    // Log API errors
    logger.apiError('HTTP request failed', error, {
      status: error.response?.status,
      statusText: error.response?.statusText,
      url: error.config?.url,
      method: error.config?.method
    });

    return Promise.reject(error);
  }
);

// Registration Types (Phase 3)
export interface PatientRegisterRequest {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  phone?: string;
  consent_given: boolean;
  tos_accepted: boolean;
}

export interface ClinicianRegisterRequest {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  specialty: string;
  license_number: string;
  phone?: string;
  profile_bio?: string;
  consent_given: boolean;
  tos_accepted: boolean;
}

export interface PublicClinician {
  id: number;
  first_name: string;
  last_name: string;
  specialty: string;
  specialties: string[];
  profile_photo_url?: string;
  booking_slug: string;
}

export interface PublicClinicianProfile {
  id: number;
  first_name: string;
  last_name: string;
  specialty: string;
  specialties: string[];
  license_number: string;
  profile_bio?: string;
  profile_photo_url?: string;
  booking_slug: string;
  // Phase 3d: profile enrichment
  video_intro_url?: string | null;
  website_url?: string | null;
  booking_mode: 'time' | 'service';
  services: ClinicianService[];
  // Phase 4 access control fields
  is_public: boolean;
  requires_booking_approval: boolean;
}

export interface PaginatedClinicianList {
  items: PublicClinician[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

export const authAPI = {
  login: async (credentials: LoginRequest): Promise<AuthResponse> => {
    const response = await api.post('/auth/login', credentials);
    return response.data;
  },

  register: async (userData: RegisterRequest): Promise<User> => {
    const response = await api.post('/auth/register', userData);
    return response.data;
  },

  // Phase 3: Role-specific registration
  registerPatient: async (userData: PatientRegisterRequest): Promise<User> => {
    const response = await api.post('/auth/register/patient', userData);
    return response.data;
  },

  registerClinician: async (userData: ClinicianRegisterRequest): Promise<User> => {
    const response = await api.post('/auth/register/clinician', userData);
    return response.data;
  },

  logout: async (): Promise<void> => {
    await api.post('/auth/logout');
  },

  getCurrentUser: async (): Promise<User> => {
    const response = await api.get('/auth/me');
    return response.data;
  },

  getAllUsers: async (): Promise<User[]> => {
    const response = await api.get('/auth/users');
    return response.data;
  },

  getUserById: async (userId: number): Promise<User> => {
    const response = await api.get(`/auth/users/${userId}`);
    return response.data;
  },

  createUser: async (userData: RegisterRequest): Promise<User> => {
    const response = await api.post('/auth/users', userData);
    return response.data;
  },

  updateUser: async (userId: number, userData: UserUpdateRequest): Promise<User> => {
    const response = await api.put(`/auth/users/${userId}`, userData);
    return response.data;
  },

  // Permanently remove an account with no booking/billing history (rejected or
  // abandoned sign-ups). The server refuses (409) if there is history.
  deleteUser: async (userId: number): Promise<{ message: string; user_id: number }> => {
    const response = await api.delete(`/auth/users/${userId}`);
    return response.data;
  },

  deactivateUser: async (userId: number): Promise<User> => {
    const response = await api.patch(`/auth/users/${userId}/deactivate`);
    return response.data;
  },

  activateUser: async (userId: number): Promise<User> => {
    const response = await api.patch(`/auth/users/${userId}/activate`);
    return response.data;
  },

  createAdmin: async (userData: RegisterRequest): Promise<User> => {
    const response = await api.post('/auth/create-admin', userData);
    return response.data;
  },

  // Password Reset (Day 3 - Security)
  forgotPassword: async (email: string): Promise<{ message: string }> => {
    const response = await api.post('/auth/forgot-password', { email });
    return response.data;
  },

  resetPassword: async (token: string, newPassword: string): Promise<{ message: string }> => {
    const response = await api.post('/auth/reset-password', {
      token,
      new_password: newPassword,
    });
    return response.data;
  },

  validateResetToken: async (token: string): Promise<{ valid: boolean; message: string }> => {
    const response = await api.post('/auth/validate-reset-token', { token });
    return response.data;
  },
};

export const bookingAPI = {
  createBooking: async (booking: BookingRequest): Promise<BookingResponse> => {
    const response = await api.post('/bookings/', booking);
    return response.data;
  },

  getAvailability: async (slot?: string): Promise<Availability | Record<string, Availability>> => {
    const url = slot ? `/bookings/availability/${slot}` : '/bookings/availability';
    const response = await api.get(url);
    return response.data;
  },

  getSchedule: async () => {
    const response = await api.get('/bookings/schedule');
    return response.data;
  },

  resetSchedule: async () => {
    const response = await api.post('/bookings/reset');
    return response.data;
  },
};

// FastAPI reads repeated keys (weekdays=0&weekdays=2) for list params, not the
// axios default weekdays[]=0 — so build the query string by hand.
const buildRangeQuery = (params: RemoveAvailabilityRangeParams): string => {
  const q = new URLSearchParams();
  q.set('start_date', params.start_date);
  q.set('end_date', params.end_date);
  if (params.start_time && params.end_time) {
    q.set('start_time', params.start_time.length === 5 ? `${params.start_time}:00` : params.start_time);
    q.set('end_time', params.end_time.length === 5 ? `${params.end_time}:00` : params.end_time);
  }
  (params.weekdays || []).forEach(d => q.append('weekdays', String(d)));
  return q.toString();
};

export const availabilityAPI = {
  // Global Availability (Admin)
  getGlobalSchedule: async (): Promise<GlobalSchedule> => {
    const response = await api.get('/availability/global');
    return response.data;
  },

  createGlobalAvailability: async (
    dayOfWeek: DayOfWeek,
    startTime: string,
    endTime: string
  ): Promise<GlobalAvailability> => {
    const response = await api.post(
      `/availability/global?day_of_week=${dayOfWeek}&start_time=${startTime}&end_time=${endTime}`
    );
    return response.data;
  },

  removeGlobalAvailability: async (dayOfWeek: DayOfWeek): Promise<void> => {
    await api.delete(`/availability/global/${dayOfWeek}`);
  },

  // Member Availability
  getMyWeeklyAvailability: async (weekStart: string): Promise<WeeklyAvailability> => {
    const response = await api.get(`/availability/member/me/weekly?week_start=${weekStart}`);
    return response.data;
  },

  getMemberWeeklyAvailability: async (memberId: number, weekStart: string): Promise<WeeklyAvailability> => {
    const response = await api.get(`/availability/member/${memberId}/weekly?week_start=${weekStart}`);
    return response.data;
  },

  createMyAvailability: async (availabilityData: CreateMemberAvailabilityRequest): Promise<MemberAvailability> => {
    // Format the data to match backend expectations
    const formattedData = {
      date: availabilityData.date,
      start_time: availabilityData.start_time,
      end_time: availabilityData.end_time,
      recurrence_type: availabilityData.recurrence_type,
      recurrence_end_date: availabilityData.recurrence_end_date && availabilityData.recurrence_end_date.trim() !== '' ? availabilityData.recurrence_end_date : null,
      notes: availabilityData.notes && availabilityData.notes.trim() !== '' ? availabilityData.notes : null
    };
    
    const response = await api.post('/availability/member/me', formattedData);
    return response.data;
  },

  createBulkAvailability: async (bulkRequest: BulkAvailabilityRequest): Promise<MemberAvailability[]> => {
    const response = await api.post('/availability/member/me/bulk', bulkRequest);
    return response.data;
  },

  removeMyAvailability: async (availabilityId: number): Promise<void> => {
    await api.delete(`/availability/member/me/${availabilityId}`);
  },

  removeMyAvailabilityBlock: async (blockId: number): Promise<void> => {
    await api.delete(`/availability/member/me/block/${blockId}`);
  },

  // Bulk-remove availability over a date range. Booked slots are always kept
  // (so a day can be cleared around a client booking); breaks are untouched.
  // weekdays + start/end_time scope it to one recurring pattern.
  removeMyAvailabilityRange: async (params: RemoveAvailabilityRangeParams): Promise<AvailabilityRangeDeleteResult> => {
    const response = await api.delete(`/availability/member/me/blocks?${buildRangeQuery(params)}`);
    return response.data;
  },

  // Admin: same as above for any member.
  removeMemberAvailabilityRange: async (memberId: number, params: RemoveAvailabilityRangeParams): Promise<AvailabilityRangeDeleteResult> => {
    const response = await api.delete(`/availability/member/${memberId}/blocks?${buildRangeQuery(params)}`);
    return response.data;
  },

  // Breaks (Module A) — non-bookable time off
  createBreak: async (breakData: BreakCreateRequest): Promise<BreakCreateResponse> => {
    const response = await api.post('/availability/member/me/breaks', breakData);
    return response.data;
  },

  getMyBreaks: async (start: string, end: string): Promise<BreakOccurrence[]> => {
    const response = await api.get(`/availability/member/me/breaks?start=${start}&end=${end}`);
    return response.data;
  },

  deleteBreak: async (groupId: string, occurrenceDate?: string): Promise<void> => {
    const query = occurrenceDate ? `?occurrence_date=${occurrenceDate}` : '';
    await api.delete(`/availability/member/me/breaks/${groupId}${query}`);
  },

  checkMyAvailabilityConflicts: async (
    targetDate: string,
    startTime: string,
    endTime: string
  ): Promise<AvailabilityConflict> => {
    const response = await api.get(
      `/availability/member/me/conflicts?target_date=${targetDate}&start_time=${startTime}&end_time=${endTime}`
    );
    return response.data;
  },

  getMyAvailableDates: async (startDate: string, endDate: string): Promise<AvailabilityDateSummary[]> => {
    const response = await api.get(
      `/availability/member/me/available-dates?start_date=${startDate}&end_date=${endDate}`
    );
    return response.data;
  },

  // Utility
  // Clinic hours for every open weekday in one call (closed days are absent).
  getGlobalHours: async (): Promise<Record<string, { start_time: string; end_time: string }>> => {
    const response = await api.get('/availability/global/hours');
    return response.data;
  },

  getGlobalHoursForDay: async (dayOfWeek: DayOfWeek): Promise<{
    day_of_week: string;
    start_time: string;
    end_time: string;
    is_active: boolean;
  }> => {
    const response = await api.get(`/availability/global/hours/${dayOfWeek}`);
    return response.data;
  },
};

export const roomAPI = {
  // Get all rooms with statistics
  getAllRooms: async (): Promise<RoomWithStats[]> => {
    const response = await api.get('/rooms/');
    return response.data;
  },

  // Get a specific room by ID
  getRoom: async (roomId: number): Promise<Room> => {
    const response = await api.get(`/rooms/${roomId}`);
    return response.data;
  },

  // Create a new room (admin only)
  createRoom: async (roomData: RoomCreateRequest): Promise<Room> => {
    const response = await api.post('/rooms/', roomData);
    return response.data;
  },

  // Update an existing room (admin only)
  updateRoom: async (roomId: number, roomData: RoomUpdateRequest): Promise<Room> => {
    const response = await api.put(`/rooms/${roomId}`, roomData);
    return response.data;
  },

  // Delete a room (admin only)
  deleteRoom: async (roomId: number): Promise<void> => {
    await api.delete(`/rooms/${roomId}`);
  },
};

// Patient Booking API
export const patientBookingAPI = {
  // Get patient's assigned clinician
  getMyClinician: async (): Promise<{
    id: number;
    name: string;
    email: string;
    specialty?: string;
    license_number?: string;
  }> => {
    const response = await api.get('/patient/bookings/my-clinician');
    return response.data;
  },

  // Get clinician availability for booking (30-min slots)
  // num_attendees affects has_room calculation (rooms filtered by capacity)
  getAvailability: async (daysAhead: number = 7, numAttendees: number = 2): Promise<ClinicianAvailabilitySlot[]> => {
    const response = await api.get(`/patient/bookings/availability?days_ahead=${daysAhead}&num_attendees=${numAttendees}`);
    return response.data;
  },

  // Create a new booking (duration in minutes)
  createBooking: async (bookingData: PatientBookingCreate): Promise<PatientBookingResponse> => {
    const response = await api.post('/patient/bookings/', bookingData);
    return response.data;
  },

  // Get all patient's bookings
  getMyBookings: async (): Promise<BookingListItem[]> => {
    const response = await api.get('/patient/bookings/');
    return response.data;
  },

  // Cancel a booking
  cancelBooking: async (bookingId: number): Promise<{ message: string; late_cancellation?: boolean; window_hours?: number }> => {
    const response = await api.delete(`/patient/bookings/${bookingId}`);
    return response.data;
  },

  // Validate booking time (duration in minutes)
  validateBookingTime: async (startTime: string, durationMinutes: number = 60): Promise<{
    valid: boolean;
    error?: string;
    message?: string;
  }> => {
    const response = await api.get('/patient/bookings/validate-time', {
      params: {
        start_time: startTime,
        duration_minutes: durationMinutes
      }
    });
    return response.data;
  },
};

// Member Booking API
export const memberBookingAPI = {
  // Get member's (clinician's) bookings
  getMyBookings: async (): Promise<BookingListItem[]> => {
    const response = await api.get('/member/bookings/');
    return response.data;
  },

  // Create a self-booking (clinician reserves a studio for themselves).
  // Optional client_name/client_email: guest client (no account) who should
  // receive the booking confirmation + reminder emails.
  createSelfBooking: async (data: {
    start_time: string;
    duration_minutes: number;
    num_attendees?: number;
    notes?: string;
    client_name?: string;
    client_email?: string;
  }): Promise<any> => {
    const response = await api.post('/member/bookings/self-book', data);
    return response.data;
  },

  // Book a pod (individual workspace at flat rate)
  bookPod: async (data: {
    start_time: string;
    duration_minutes: number;
    notes?: string;
  }): Promise<any> => {
    const response = await api.post('/member/bookings/book-pod', data);
    return response.data;
  },

  // Cancel one of the clinician's own room bookings (self-booking or pod).
  // Outside the cancellation window the room is released (not billed); inside
  // it the cancel is allowed but the room stays held and billed.
  cancelSelfBooking: async (bookingId: number, reason?: string): Promise<{
    message: string;
    booking_id: number;
    released: boolean;
    within_window: boolean;
    window_hours: number;
  }> => {
    const response = await api.delete(`/member/bookings/${bookingId}`, {
      params: reason ? { reason } : undefined,
    });
    return response.data;
  },

  // Add 15-minute buffer to a booking
  extendBuffer: async (bookingId: number): Promise<any> => {
    const response = await api.post(`/member/bookings/${bookingId}/extend-buffer`);
    return response.data;
  },

  // Clinician cancel of a client booking (cancellation-window rule). The server is
  // authoritative on the boundary; boundary_crossed=true means a release
  // request was downgraded to keep-room because the window had passed.
  cancelBooking: async (
    bookingId: number,
    data: { reason: string; internal_note?: string; release_room: boolean },
  ): Promise<{
    message: string;
    booking_id: number;
    within_window: boolean;
    window_hours: number;
    released: boolean;
    boundary_crossed: boolean;
  }> => {
    const response = await api.post(`/member/bookings/${bookingId}/cancel`, data);
    return response.data;
  },
};

// Member Profile API (Phase 3)
export interface ProfileUpdateRequest {
  first_name?: string;
  last_name?: string;
  specialty?: string;
  license_number?: string;
  phone?: string;
  profile_bio?: string;
  is_public?: boolean;
  requires_booking_approval?: boolean;  // Phase 4: Public + Approval mode
  specialties?: string[];  // Phase 3c: searchable specialty tags (max 10)
  // Phase 3d: profile enrichment
  video_intro_url?: string | null;
  website_url?: string | null;
  preferred_room_id?: number | null;
  booking_mode?: 'time' | 'service';  // service-based booking toggle
}

// Phase 3d: clinician service offerings
export interface ClinicianService {
  id: number;
  clinician_id: number;
  name: string;
  duration_minutes: number;
  capacity: number;
  price?: number | string | null;
  description?: string | null;
  display_order: number;
}

export interface ClinicianServiceInput {
  name: string;
  duration_minutes: number;
  capacity?: number;
  price?: number | string | null;
  description?: string | null;
  display_order?: number;
}

export interface RoomOption {
  id: number;
  name: string;
  capacity: number;
  room_type: string;
}

export interface SlugRegenerateResponse {
  old_slug: string;
  new_slug: string;
  message: string;
}

export interface BookingLinkResponse {
  booking_url: string;
  slug: string;
}

export interface PhotoUploadResponse {
  message: string;
  photo_url: string;
}

export const memberProfileAPI = {
  // Get current clinician's profile
  getProfile: async (): Promise<User> => {
    const response = await api.get('/member/profile');
    return response.data;
  },

  // Update clinician profile
  updateProfile: async (data: ProfileUpdateRequest): Promise<User> => {
    const response = await api.put('/member/profile', data);
    return response.data;
  },

  // Regenerate booking slug
  regenerateSlug: async (): Promise<SlugRegenerateResponse> => {
    const response = await api.post('/member/regenerate-slug');
    return response.data;
  },

  // Get booking link
  getBookingLink: async (): Promise<BookingLinkResponse> => {
    const response = await api.get('/member/booking-link');
    return response.data;
  },

  // Upload profile photo
  uploadProfilePhoto: async (file: File): Promise<PhotoUploadResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/member/profile/photo', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Delete profile photo
  deleteProfilePhoto: async (): Promise<{ message: string }> => {
    const response = await api.delete('/member/profile/photo');
    return response.data;
  },

  // Phase 3d: active rooms (for the preferred-room dropdown)
  listRooms: async (): Promise<RoomOption[]> => {
    const response = await api.get('/member/rooms');
    return response.data;
  },

  // Phase 3d: service offerings CRUD
  listServices: async (): Promise<ClinicianService[]> => {
    const response = await api.get('/member/services');
    return response.data;
  },
  createService: async (data: ClinicianServiceInput): Promise<ClinicianService> => {
    const response = await api.post('/member/services', data);
    return response.data;
  },
  updateService: async (id: number, data: Partial<ClinicianServiceInput>): Promise<ClinicianService> => {
    const response = await api.put(`/member/services/${id}`, data);
    return response.data;
  },
  deleteService: async (id: number): Promise<void> => {
    await api.delete(`/member/services/${id}`);
  },

  // Calendar feed (iCal outward sync)
  getCalendarFeed: async (): Promise<CalendarFeedResponse> => {
    const response = await api.get('/member/calendar-feed');
    return response.data;
  },

  regenerateCalendarFeed: async (): Promise<CalendarFeedResponse> => {
    const response = await api.post('/member/calendar-feed/regenerate');
    return response.data;
  },
};

// Calendar feed types + absolute-URL helper
export interface CalendarFeedResponse {
  enabled: boolean;
  feed_path: string;            // API-relative, e.g. /api/v1/feeds/ical/{token}.ics
  last_accessed_at: string | null;
  access_count: number;
}

/** Compose the absolute subscription URL from the API-relative feed path. */
export const buildCalendarFeedUrl = (feedPath: string): string =>
  new URL(feedPath, API_BASE_URL).toString();

// Public policy / rollout switches — read once at app load (PolicyContext)
export const configAPI = {
  getPolicy: async (): Promise<{
    cancellation_window_hours: number;
    booking_grace_minutes: number;
    self_cancel_enabled: boolean;
    guides_enabled: boolean;
    cancellation_flag_threshold: number;
    cancellation_flag_days: number;
  }> => {
    const response = await api.get('/config/policy');
    return response.data;
  },
};

// Admin Analytics API — read-only snapshots (every response carries `as_of`)
export const adminAnalyticsAPI = {
  getSnapshot: async (period?: string): Promise<AnalyticsSnapshot> => {
    const response = await api.get('/admin/analytics/snapshot', { params: period ? { period } : undefined });
    return response.data;
  },
  getCancellations: async (start?: string, end?: string): Promise<CancellationReport> => {
    const response = await api.get('/admin/analytics/cancellations', { params: { start, end } });
    return response.data;
  },
  getCalendar: async (start: string, end: string): Promise<ClinicCalendar> => {
    const response = await api.get('/admin/analytics/calendar', { params: { start, end } });
    return response.data;
  },
};

// Admin Booking API
export const adminBookingAPI = {
  // Get all bookings in the system
  getAllBookings: async (): Promise<BookingListItem[]> => {
    const response = await api.get('/member/bookings/all');
    return response.data;
  },

  // --- Phase 1b admin ops ---
  resendConfirmation: async (bookingId: number, reason: string): Promise<any> => {
    const response = await api.post(`/admin/bookings/${bookingId}/resend-confirmation`, { reason });
    return response.data;
  },

  // newDurationMinutes: optional — also change the appointment length (15-min
  // increments). Blocks the booking no longer covers go back to "available".
  reschedule: async (bookingId: number, newStartTime: string, reason: string, newDurationMinutes?: number): Promise<any> => {
    const response = await api.post(`/admin/bookings/${bookingId}/reschedule`, {
      new_start_time: newStartTime,
      reason,
      ...(newDurationMinutes ? { new_duration_minutes: newDurationMinutes } : {}),
    });
    return response.data;
  },

  removeBuffer: async (bookingId: number, reason: string): Promise<any> => {
    const response = await api.post(`/admin/bookings/${bookingId}/remove-buffer`, { reason });
    return response.data;
  },

  reassignRoom: async (bookingId: number, newRoomId: number, reason: string): Promise<any> => {
    const response = await api.post(`/admin/bookings/${bookingId}/reassign-room`, {
      new_room_id: newRoomId,
      reason,
    });
    return response.data;
  },

  forceCancel: async (
    bookingId: number,
    data: { reason: string; internal_note?: string; release_room: boolean },
  ): Promise<{ message: string; booking_id: number; released: boolean }> => {
    const response = await api.post(`/admin/bookings/${bookingId}/cancel`, data);
    return response.data;
  },
};

// Admin notification-log API (Phase 1c) — outbound queue visibility + requeue
export interface AdminNotification {
  id: number;
  channel: string;
  recipient_email: string | null;
  recipient_phone: string | null;
  subject: string | null;
  template_name: string;
  status: string;
  attempt_count: number;
  scheduled_for: string | null;
  last_attempt_at: string | null;
  next_retry_at: string | null;
  error_message: string | null;
  cancellation_reason: string | null;
  azure_message_id: string | null;
  user_id: number | null;
  booking_id: number | null;
  created_at: string | null;
  sent_at: string | null;
}

export interface AdminNotificationDetail extends AdminNotification {
  context_json: Record<string, any>;
}

export interface AdminNotificationListResponse {
  total: number;
  limit: number;
  offset: number;
  items: AdminNotification[];
}

export interface AdminNotificationHealth {
  healthy: boolean;
  stuck_count: number;
  threshold_minutes: number;
  by_status: Record<string, number>;
}

export interface AdminNotificationFilters {
  status?: string;
  channel?: string;
  template_name?: string;
  recipient?: string;
  booking_id?: number;
  date_from?: string;
  date_to?: string;
  limit?: number;
  offset?: number;
}

export const adminNotificationsAPI = {
  list: async (filters: AdminNotificationFilters = {}): Promise<AdminNotificationListResponse> => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') params.append(key, String(value));
    });
    const qs = params.toString();
    const response = await api.get(`/admin/notifications${qs ? `?${qs}` : ''}`);
    return response.data;
  },

  getDetail: async (id: number): Promise<AdminNotificationDetail> => {
    const response = await api.get(`/admin/notifications/${id}`);
    return response.data;
  },

  requeue: async (id: number): Promise<AdminNotificationDetail> => {
    const response = await api.post(`/admin/notifications/${id}/requeue`);
    return response.data;
  },

  health: async (): Promise<AdminNotificationHealth> => {
    const response = await api.get('/admin/notifications/health');
    return response.data;
  },
};

// Notification API
export const notificationAPI = {
  // Get notifications with filters
  getNotifications: async (timeRange: string = 'week', unreadOnly: boolean = false, limit: number = 20) => {
    const params = new URLSearchParams({
      time_range: timeRange,
      unread_only: unreadOnly.toString(),
      limit: limit.toString()
    });
    const response = await api.get(`/notifications/?${params}`);
    return response.data;
  },

  // Get unread notification count
  getUnreadCount: async () => {
    const response = await api.get('/notifications/unread-count');
    return response.data;
  },

  // Mark notification as read
  markAsRead: async (notificationId: number) => {
    const response = await api.patch(`/notifications/${notificationId}`, { is_read: true });
    return response.data;
  },

  // Mark all notifications as read
  markAllAsRead: async () => {
    const response = await api.patch('/notifications/mark-all-read');
    return response.data;
  },

  // Delete notification
  deleteNotification: async (notificationId: number) => {
    const response = await api.delete(`/notifications/${notificationId}`);
    return response.data;
  },
};

// Extended Public Clinician Profile with Phase 4 fields
export interface ExtendedPublicClinicianProfile extends PublicClinicianProfile {
  is_public: boolean;
  requires_booking_approval: boolean;
}

// Public Clinician Directory API (Phase 3 + Phase 4)
export const clinicianAPI = {
  // List public, approved clinicians (no auth required)
  listClinicians: async (
    page: number = 1,
    limit: number = 20,
    specialty?: string,
    search?: string
  ): Promise<PaginatedClinicianList> => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (specialty) {
      params.append('specialty', specialty);
    }
    if (search && search.trim()) {
      params.append('search', search.trim());
    }
    const response = await api.get(`/clinicians?${params}`);
    return response.data;
  },

  // Get clinician profile by slug (no auth required)
  getClinicianBySlug: async (slug: string): Promise<PublicClinicianProfile> => {
    const response = await api.get(`/clinicians/${slug}`);
    return response.data;
  },

  // Get clinician availability by slug (Phase 4 - requires patient auth)
  // Used for multi-clinician booking flow
  getClinicianAvailability: async (
    slug: string,
    daysAhead: number = 7,
    numAttendees: number = 2
  ): Promise<ClinicianAvailabilitySlot[]> => {
    const response = await api.get(
      `/clinicians/${slug}/availability?days_ahead=${daysAhead}&num_attendees=${numAttendees}`
    );
    return response.data;
  },
};

// Admin API (Phase 3)
export const adminAPI = {
  // Get pending clinician registrations
  getPendingClinicians: async (): Promise<User[]> => {
    const response = await api.get('/admin/pending-clinicians');
    return response.data;
  },

  // Approve a clinician
  approveClinician: async (clinicianId: number): Promise<User> => {
    const response = await api.post(`/admin/clinicians/${clinicianId}/approve`);
    return response.data;
  },

  // Reject a clinician
  rejectClinician: async (clinicianId: number, reason?: string): Promise<User> => {
    const response = await api.post(`/admin/clinicians/${clinicianId}/reject`, { reason });
    return response.data;
  },
};

// Phase 4: Patient Access API Types
export interface RedeemCodeRequest {
  code: string;
}

export interface RedeemCodeResponse {
  message: string;
  clinician_id: number;
  clinician_name: string;
  clinician_specialty: string;
  booking_slug?: string;
}

export interface ConnectedClinician {
  clinician_id: number;
  clinician_name: string;
  clinician_specialty: string;
  booking_slug?: string;
  profile_photo_url?: string;
  connection_source: string;
  connected_at: string;
  can_book: boolean;
  requires_booking_approval: boolean;  // Phase 4: Public + Approval mode
  is_public: boolean;                  // Phase 4: Clinician visibility mode
  booking_mode: 'time' | 'service';    // Phase 3d: service-based booking
  services: ClinicianService[];        // Phase 3d: service menu
}

export interface MyClinicianListResponse {
  items: ConnectedClinician[];
  total: number;
}

// Phase 4: Patient Access API
export const patientAccessAPI = {
  // Redeem an invite code to connect with a clinician
  redeemCode: async (code: string): Promise<RedeemCodeResponse> => {
    const response = await api.post('/patient/redeem-code', { code });
    return response.data;
  },

  // List all clinicians the patient is connected to
  getMyClinicians: async (): Promise<MyClinicianListResponse> => {
    const response = await api.get('/patient/my-clinicians');
    return response.data;
  },

  // Get a specific connected clinician by slug (works for both public and private clinicians)
  // For private clinicians, requires active PatientClinicianAccess
  getConnectedClinicianBySlug: async (slug: string): Promise<ConnectedClinician> => {
    const response = await api.get(`/patient/my-clinicians/${slug}`);
    return response.data;
  },

  // Disconnect from a clinician
  disconnectFromClinician: async (clinicianId: number): Promise<{ message: string }> => {
    const response = await api.delete(`/patient/my-clinicians/${clinicianId}`);
    return response.data;
  },
};

// Phase 4: Booking Request API Types (Public + Approval Mode)
export interface PendingBookingRequestCreate {
  clinician_id: number;
  start_time: string;
  duration_minutes: number;
  num_attendees: number;
  service_id?: number;        // Service-based booking: duration/attendees derived server-side
  notes?: string;
}

export interface PendingBookingRequest {
  id: number;
  patient_id: number;
  patient_name: string;
  patient_email: string;
  clinician_id: number;
  clinician_name: string;
  clinician_specialty?: string;
  start_time: string;
  duration_minutes: number;
  num_attendees: number;
  notes?: string;
  approval_status: string;
  approval_requested_at: string;
  approval_responded_at?: string;
  rejection_reason?: string;
  rejection_message?: string;
  expires_at: string;
  is_expired: boolean;
  slot_still_available: boolean;
  created_at: string;
}

export interface PendingBookingRequestListResponse {
  requests: PendingBookingRequest[];
  total: number;
  pending_count: number;
  approved_count: number;
  rejected_count: number;
  expired_count: number;
}

export interface BookingRequestApprovalResponse {
  message: string;
  booking_id: number;
  booking_status: string;
  room_id?: number;
  room_name?: string;
  start_time: string;
  duration_minutes: number;
}

export interface BookingRequestRejectionResponse {
  message: string;
  request_id: number;
  rejection_reason: string;
  patient_message: string;
}

export interface RejectionReason {
  key: string;
  message: string;
}

export interface RejectionReasonsResponse {
  reasons: RejectionReason[];
}

// Phase 4: Booking Request API (Member/Clinician)
export const bookingRequestAPI = {
  // Get all booking requests for the current clinician
  getClinicianRequests: async (includeResolved: boolean = false): Promise<PendingBookingRequestListResponse> => {
    const response = await api.get(`/member/booking-requests?include_resolved=${includeResolved}`);
    return response.data;
  },

  // Approve a booking request
  approveRequest: async (requestId: number): Promise<BookingRequestApprovalResponse> => {
    const response = await api.post(`/member/booking-requests/${requestId}/approve`);
    return response.data;
  },

  // Reject a booking request
  rejectRequest: async (requestId: number, reason: string, internalNote?: string): Promise<BookingRequestRejectionResponse> => {
    const response = await api.post(`/member/booking-requests/${requestId}/reject`, {
      reason,
      internal_note: internalNote,
    });
    return response.data;
  },

  // Get rejection reasons for dropdown
  getRejectionReasons: async (): Promise<RejectionReasonsResponse> => {
    const response = await api.get('/member/booking-requests/rejection-reasons');
    return response.data;
  },
};

// Phase 4: Booking Request API (Patient)
export const patientBookingRequestAPI = {
  // Create a booking request for a Public + Approval clinician
  createRequest: async (data: PendingBookingRequestCreate): Promise<PendingBookingRequest> => {
    const response = await api.post('/patient/booking-requests', data);
    return response.data;
  },

  // Get patient's booking requests
  getMyRequests: async (includeResolved: boolean = false): Promise<PendingBookingRequest[]> => {
    const response = await api.get(`/patient/booking-requests?include_resolved=${includeResolved}`);
    return response.data;
  },

  // Cancel a pending booking request
  cancelRequest: async (requestId: number): Promise<{ message: string }> => {
    const response = await api.delete(`/patient/booking-requests/${requestId}`);
    return response.data;
  },
};

// Phase 4: Invite Code API Types
export interface InviteCodeCreate {
  intended_email?: string;
  label?: string;
  expires_in_days?: number;
}

export interface InviteCode {
  id: number;
  code: string;
  formatted_code: string;
  intended_email?: string;
  label?: string;
  max_uses: number;
  times_used: number;
  expires_at: string;
  is_active: boolean;
  is_valid: boolean;
  revoked_at?: string;
  created_at: string;
}

export interface InviteCodeListResponse {
  items: InviteCode[];
  total: number;
  active_count: number;
}

// Phase 4: Invite Code API
export const inviteCodeAPI = {
  // Generate a new invite code
  createInviteCode: async (data: InviteCodeCreate = {}): Promise<InviteCode> => {
    const response = await api.post('/member/invite-codes', data);
    return response.data;
  },

  // List all invite codes for the current clinician
  listInviteCodes: async (activeOnly: boolean = false): Promise<InviteCodeListResponse> => {
    const response = await api.get(`/member/invite-codes?active_only=${activeOnly}`);
    return response.data;
  },

  // Revoke an invite code
  revokeInviteCode: async (codeId: number): Promise<{ message: string }> => {
    const response = await api.delete(`/member/invite-codes/${codeId}`);
    return response.data;
  },
};

// Phase 5: Member Billing API Types
export interface BillingStatus {
  billing_setup_complete: boolean;
  stripe_customer_id: string | null;
  has_payment_method: boolean;
}

export interface SetupIntentResponse {
  client_secret: string;
  stripe_customer_id: string;
}

export interface UsageSummary {
  billing_period: string;
  total_hours: number;
  peak_hours: number;
  standard_hours: number;
  off_peak_hours: number;
  pod_hours: number;
  pod_amount: number;
  user_type: string;
  estimated_amount: number;
}

export interface Invoice {
  id: number;
  billing_period: string;
  user_type: string;
  total_hours: number;
  peak_hours: number;
  standard_hours: number;
  off_peak_hours: number;
  peak_amount: number;
  standard_amount: number;
  off_peak_amount: number;
  membership_fee: number;
  credit_amount: number;
  total_amount: number;
  stripe_invoice_id: string | null;
  stripe_invoice_url: string | null;
  stripe_pdf_url: string | null;
  status: string;
  created_at: string;
}

export interface InvoiceListResponse {
  items: Invoice[];
  total: number;
}

export interface ConfirmSetupResponse {
  message: string;
  billing_setup_complete: boolean;
}

// Phase 5: Member Billing API
export const memberBillingAPI = {
  // Per-booking lines behind my usage for a period (default: current month)
  getUsageDetail: async (billingPeriod?: string): Promise<UsageDetail> => {
    const response = await api.get('/member/billing/usage/detail', {
      params: billingPeriod ? { billing_period: billingPeriod } : {},
    });
    return response.data;
  },
  // Get billing status
  getStatus: async (): Promise<BillingStatus> => {
    const response = await api.get('/member/billing/status');
    return response.data;
  },

  // Create SetupIntent for card collection
  createSetupIntent: async (): Promise<SetupIntentResponse> => {
    const response = await api.post('/member/billing/setup-intent');
    return response.data;
  },

  // Confirm billing setup after Stripe Elements completes
  confirmSetup: async (): Promise<ConfirmSetupResponse> => {
    const response = await api.post('/member/billing/confirm-setup');
    return response.data;
  },

  // Get current month usage summary
  getCurrentUsage: async (): Promise<UsageSummary> => {
    const response = await api.get('/member/billing/usage/current');
    return response.data;
  },

  // Get invoice history
  getInvoices: async (limit: number = 12): Promise<InvoiceListResponse> => {
    const response = await api.get(`/member/billing/invoices?limit=${limit}`);
    return response.data;
  },

  // Get single invoice detail
  getInvoice: async (invoiceId: number): Promise<Invoice> => {
    const response = await api.get(`/member/billing/invoices/${invoiceId}`);
    return response.data;
  },

  // Download invoice PDF
  downloadInvoicePdf: async (invoiceId: number): Promise<Blob> => {
    const response = await api.get(`/member/billing/invoices/${invoiceId}/pdf`, {
      responseType: 'blob',
    });
    return response.data;
  },
};

// Phase 5: Admin Billing API Types
export interface AdminInvoice {
  id: number;
  clinician_id: number;
  clinician_name: string;
  clinician_email: string;
  billing_period: string;
  user_type: string;
  total_hours: number;
  subtotal: number;
  credit_amount: number;
  tax_amount: number;
  total_amount: number;
  status: string;
  stripe_invoice_id: string | null;
  stripe_invoice_url: string | null;
  created_at: string;
}

export interface AdminInvoiceDetail extends AdminInvoice {
  peak_hours: number;
  standard_hours: number;
  off_peak_hours: number;
  peak_rate: number;
  standard_rate: number;
  off_peak_rate: number;
  peak_amount: number;
  standard_amount: number;
  off_peak_amount: number;
  membership_fee: number;
  stripe_pdf_url: string | null;
}

export interface InvoicePreview {
  clinician_id: number;
  clinician_name: string;
  clinician_email: string;
  billing_period: string;
  billing_setup_complete: boolean;
  stripe_customer_id: string | null;
  user_type: string;
  total_hours: number;
  peak_hours: number;
  standard_hours: number;
  off_peak_hours: number;
  peak_rate: number;
  standard_rate: number;
  off_peak_rate: number;
  peak_amount: number;
  standard_amount: number;
  off_peak_amount: number;
  pod_hours: number;
  pod_amount: number;
  membership_fee: number;
  subtotal: number;
  credit_amount: number;
  tax_amount: number;
  total_amount: number;
}

export interface InvoiceGenerationResult {
  billing_period: string;
  total_clinicians: number;
  invoices_created: number;
  invoices_skipped: number;
  invoices_existing: number;
  errors: Array<{ clinician_id: number; error: string }>;
}

export interface BillingPeriodSummary {
  billing_period: string;
  total_clinicians: number;
  total_invoices: number;
  total_revenue: number;
  pending_count: number;
  open_count: number;  // Invoices sent to Stripe, awaiting payment
  paid_count: number;
  failed_count: number;
}

export interface TimeSlot {
  id: number;
  day_of_week: string;
  start_time: string;
  end_time: string;
  category: string;
  is_active: boolean;
}

export interface BillingRate {
  id: number;
  user_type: string;
  time_category: string;
  hourly_rate: number;
  effective_from: string;
  effective_to: string | null;
}

export interface UserTypeThreshold {
  id: number;
  user_type: string;
  min_hours: number;
  max_hours: number | null;
  is_active: boolean;
}

export interface MembershipFee {
  id: number;
  amount: number;
  effective_from: string;
  effective_to: string | null;
}

export interface BillingConfig {
  time_slots: TimeSlot[];
  rates: BillingRate[];
  thresholds: UserTypeThreshold[];
  membership_fee: MembershipFee | null;
}

// Phase 5: Admin Billing API
// Per-booking lines behind a clinician's preview / invoice total
export interface UsageLine {
  booking_id: number;
  date: string;            // YYYY-MM-DD, clinic local
  start_time: string;      // HH:MM
  end_time: string;        // HH:MM, includes buffer
  duration_minutes: number;
  buffer_minutes: number;
  room_name: string | null;
  room_type: 'studio' | 'pod';
  booking_kind: 'client' | 'self' | 'late_cancel_held';
  client_name: string | null;
  status: string;
  peak_hours: number;
  standard_hours: number;
  off_peak_hours: number;
  pod_hours: number;
  billable_hours: number;
  rate: number | null;     // $/hr; null when the line spans categories with different rates
  amount: number;
}

export interface UsageDetail {
  clinician_id: number;
  clinician_name: string;
  clinician_email: string;
  billing_period: string;
  user_type: string;
  peak_rate: number;
  standard_rate: number;
  off_peak_rate: number;
  pod_rate_per_hour: number;
  lines: UsageLine[];
  studio_hours: number;
  pod_hours: number;
  total_hours: number;
  peak_hours: number;
  standard_hours: number;
  off_peak_hours: number;
  peak_amount: number;
  standard_amount: number;
  off_peak_amount: number;
  pod_amount: number;
  membership_fee: number;
  subtotal: number;
  tax_amount: number;
  credit_amount: number;
  total_amount: number;
  lines_amount: number;
  invoice_id: number | null;
  invoice_status: string | null;
  invoice_total_amount: number | null;
}

export const adminBillingAPI = {
  // Per-booking lines behind a clinician's preview / invoice total
  getUsageDetail: async (clinicianId: number, billingPeriod: string): Promise<UsageDetail> => {
    const response = await api.get(`/admin/billing/usage/${clinicianId}`, { params: { billing_period: billingPeriod } });
    return response.data;
  },

  // CSV of every billed booking for the period (one clinician, or all active members)
  downloadUsageCsv: async (billingPeriod: string, clinicianId?: number): Promise<Blob> => {
    const response = await api.get('/admin/billing/usage/export', {
      params: { billing_period: billingPeriod, ...(clinicianId ? { clinician_id: clinicianId } : {}) },
      responseType: 'blob',
    });
    return response.data;
  },
  // Get billing configuration (read-only for MVP)
  getConfig: async (): Promise<BillingConfig> => {
    const response = await api.get('/admin/billing/config');
    return response.data;
  },

  // Preview invoice for single clinician
  previewInvoice: async (clinicianId: number, billingPeriod: string): Promise<InvoicePreview> => {
    const response = await api.get(`/admin/billing/preview/${clinicianId}?billing_period=${billingPeriod}`);
    return response.data;
  },

  // Preview all invoices for a period
  previewAllInvoices: async (billingPeriod: string): Promise<InvoicePreview[]> => {
    const response = await api.get(`/admin/billing/preview?billing_period=${billingPeriod}`);
    return response.data;
  },

  // Generate invoices
  generateInvoices: async (billingPeriod: string, sendToStripe: boolean = true, clinicianId?: number): Promise<InvoiceGenerationResult> => {
    const response = await api.post('/admin/billing/generate', {
      billing_period: billingPeriod,
      send_to_stripe: sendToStripe,
      clinician_id: clinicianId,
    });
    return response.data;
  },

  // List invoices with filters
  getInvoices: async (billingPeriod?: string, status?: string, limit: number = 50, offset: number = 0): Promise<AdminInvoice[]> => {
    const params = new URLSearchParams();
    if (billingPeriod) params.append('billing_period', billingPeriod);
    if (status) params.append('status', status);
    params.append('limit', limit.toString());
    params.append('offset', offset.toString());
    const response = await api.get(`/admin/billing/invoices?${params}`);
    return response.data;
  },

  // Get invoice detail
  getInvoice: async (invoiceId: number): Promise<AdminInvoiceDetail> => {
    const response = await api.get(`/admin/billing/invoices/${invoiceId}`);
    return response.data;
  },

  // Get billing period summary
  getSummary: async (billingPeriod: string): Promise<BillingPeriodSummary> => {
    const response = await api.get(`/admin/billing/summary/${billingPeriod}`);
    return response.data;
  },

  // Download single invoice PDF
  downloadInvoicePdf: async (invoiceId: number): Promise<Blob> => {
    const response = await api.get(`/admin/billing/invoices/${invoiceId}/pdf`, {
      responseType: 'blob',
    });
    return response.data;
  },

  // Bulk download all invoices for a period as ZIP
  bulkDownloadInvoices: async (billingPeriod: string): Promise<Blob> => {
    const response = await api.get(`/admin/billing/invoices/export/${billingPeriod}`, {
      responseType: 'blob',
    });
    return response.data;
  },

  // Mark invoice as paid
  markInvoicePaid: async (invoiceId: number): Promise<any> => {
    const response = await api.post(`/admin/billing/invoices/${invoiceId}/mark-paid`);
    return response.data;
  },

  // --- Module B: Billing Credits ---
  createCredit: async (payload: BillingCreditCreate): Promise<BillingCredit> => {
    const response = await api.post('/admin/billing/credits', payload);
    return response.data;
  },

  getCredits: async (filters: { clinician_id?: number; period?: string; applied?: boolean } = {}): Promise<BillingCredit[]> => {
    const params = new URLSearchParams();
    if (filters.clinician_id != null) params.append('clinician_id', String(filters.clinician_id));
    if (filters.period) params.append('period', filters.period);
    if (filters.applied != null) params.append('applied', String(filters.applied));
    const qs = params.toString();
    const response = await api.get(`/admin/billing/credits${qs ? `?${qs}` : ''}`);
    return response.data;
  },

  deleteCredit: async (creditId: number): Promise<any> => {
    const response = await api.delete(`/admin/billing/credits/${creditId}`);
    return response.data;
  },
};

export interface BillingCreditCreate {
  clinician_id: number;
  amount: string;            // decimal as string
  description: string;
  target_billing_period: string;  // 'YYYY-MM'
}

export interface BillingCredit {
  id: number;
  clinician_id: number;
  clinician_name: string | null;
  amount: string;
  description: string;
  target_billing_period: string;
  applied_at: string | null;
  applied_to_invoice_id: number | null;
  created_by: number;
  created_at: string;
}

// Utility: trigger browser download from a Blob
export function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// =========================================================================
// CHECK-IN API (Phase E - Public, No Auth Required)
// =========================================================================

interface CheckinResolveResponse {
  booking_id: number;
  booking_time: string;
  booking_date: string;
  duration_minutes: number;
  clinician_first_name: string;
  already_checked_in: boolean;
}

interface CheckinLookupResponse {
  booking_id: number;
  booking_time: string;
  booking_date: string;
  duration_minutes: number;
  patient_first_name: string;
  already_checked_in: boolean;
  lookup_token: string;
}

interface CheckinConfirmRequest {
  token?: string;
  lookup_token?: string;
}

interface CheckinConfirmResponse {
  success: boolean;
  already_checked_in: boolean;
  message: string;
}

export const checkinAPI = {
  // Resolve a check-in token from email link
  resolve: async (token: string): Promise<CheckinResolveResponse> => {
    const response = await api.get(`/checkin/resolve?token=${encodeURIComponent(token)}`);
    return response.data;
  },

  // Look up nearest booking by email (kiosk flow)
  lookup: async (email: string): Promise<CheckinLookupResponse> => {
    const response = await api.post('/checkin/lookup', { email });
    return response.data;
  },

  // Confirm check-in
  confirm: async (body: CheckinConfirmRequest): Promise<CheckinConfirmResponse> => {
    const response = await api.post('/checkin/confirm', body);
    return response.data;
  },
};

// =========================================================================
// DISPLAY BOARD API (Phase F - Token Auth, No User Login)
// =========================================================================

interface DisplayBooking {
  booking_id: number;
  start_time_local: string;
  end_time_local: string;
  duration_minutes: number;
  clinician_name: string;
  clinician_first_name: string;
  clinician_photo_url: string | null;
  booking_type: 'client' | 'self' | 'pod';
  has_arrived: boolean;
  arrived_minutes_ago: number | null;
}

interface DisplayRoom {
  room_id: number;
  room_name: string;
  room_type: 'studio' | 'pod';
  capacity: number;
  bookings: DisplayBooking[];
}

interface DisplayScheduleResponse {
  rooms: DisplayRoom[];
  current_time: string;
  current_date: string;
  timezone: string;
}

interface ArrivalNotification {
  booking_id: number;
  clinician_name: string;
  booking_time: string;
  message: string;
  minutes_ago: number;
}

interface DisplayArrivalsResponse {
  arrivals: ArrivalNotification[];
}

export type { DisplayBooking, DisplayRoom, DisplayScheduleResponse, ArrivalNotification };

export const displayAPI = {
  getSchedule: async (token: string): Promise<DisplayScheduleResponse> => {
    const response = await api.get('/display/schedule', {
      headers: { 'X-Display-Token': token },
    });
    return response.data;
  },

  getArrivals: async (token: string, minutes: number = 10): Promise<DisplayArrivalsResponse> => {
    const response = await api.get(`/display/arrivals?minutes=${minutes}`, {
      headers: { 'X-Display-Token': token },
    });
    return response.data;
  },
};

// Export base api instance for direct use
export { api }; 